/**
 * @core-loop/ui — Footer component
 *
 * Shared platform footer for all core-loop.dev apps.
 * Renders platform links, legal, and locale switcher slot.
 *
 * Props:
 *   appName        {string}    Current app name
 *   locale         {string}    Current locale code
 *   theme          {string}    "dark" | "light"
 *   onToggleTheme  {Function}  Callback to toggle theme
 *   localeSwitcher {ReactNode} Optional locale switcher element rendered in footer
 *   publicApps     {Array}     Optional override for platform app links
 *
 * HC-SHARED-UI: This is the single authoritative Footer implementation.
 */

import type { ReactNode } from 'react';
import { PUBLIC_APPS } from './Header.tsx';
import type { PlatformApp } from './Header.tsx';

const CURRENT_YEAR = new Date().getFullYear();

interface FooterProps {
  appName?: string;
  locale?: string;
  theme?: string;
  onToggleTheme?: () => void;
  localeSwitcher?: ReactNode;
  publicApps?: PlatformApp[];
}

export function Footer({
  appName = 'core-loop',
  theme,
  onToggleTheme,
  localeSwitcher,
  publicApps = PUBLIC_APPS,
}: FooterProps) {
  return (
    <footer className="cl-footer" aria-label="Site footer">
      <div className="cl-footer__brand">
        <a href="https://core-loop.dev" className="cl-footer__home">core-loop.dev</a>
        {appName !== 'core-loop' && (
          <span className="cl-footer__app"> / {appName}</span>
        )}
      </div>

      <nav className="cl-footer__nav" aria-label="Footer platform links">
        {publicApps.map(({ name, href }) => (
          <a key={href} href={href} className="cl-footer__link">{name}</a>
        ))}
        <a href="https://core-loop.dev/about" className="cl-footer__link">About</a>
        <a href="https://core-loop.dev/privacy" className="cl-footer__link">Privacy</a>
      </nav>

      <div className="cl-footer__actions">
        {localeSwitcher}
        {onToggleTheme && (
          <button
            type="button"
            className="cl-theme-toggle"
            onClick={onToggleTheme}
            aria-label={theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
          >
            {theme === 'dark' ? '☀' : '☾'}
          </button>
        )}
      </div>

      <p className="cl-footer__legal">
        &copy; {CURRENT_YEAR} core-loop.dev. Built with{' '}
        <a href="https://core-loop.dev/about" className="cl-footer__link">Evidence-Driven Development</a>.
      </p>
    </footer>
  );
}
